import { supabase } from "@/lib/supabase";
import { readWorkspaceOrgIdFromStorage } from "@/lib/workspaceOrgStorage";
import type {
  MfgAuditListFilters,
  MfgAuditListResponse,
  MfgBulkCreateMaterialsResponse,
  MfgBulkImportRow,
  MfgCreateMaterialResponse,
  MfgEntitlementResponse,
  MfgRecipeCostEntryRequest,
  MfgRecipeCostEntryResponse,
  MfgRecipeInventoryListResponse,
  MfgUpdateMaterialResponse,
  MfgMaterialCostingSummaryResponse,
  MfgMaterialsResponse,
  MfgMaterialsUsedInRecipesResponse,
  MfgPlantCreateResponse,
  MfgPlantDeleteResponse,
  MfgPlantsListResponse,
  MfgPlantUpdateResponse,
  MfgUnitCreateResponse,
  MfgUnitsListResponse,
} from "@/lib/manufacturing/types";

export type * from "@/lib/manufacturing/types";

type FetchResult<T> =
  | { ok: true; status: number; data: T }
  | { ok: false; status: number; error: string; body?: unknown };

export async function manufacturingFetch<T>(path: string, init: RequestInit = {}): Promise<FetchResult<T>> {
  if (!supabase) return { ok: false, status: 503, error: "App is not configured for sign-in." };
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) return { ok: false, status: 401, error: "Sign in required." };

  const headers: HeadersInit = {
    Authorization: `Bearer ${token}`,
    ...(init.headers || {}),
  };
  const ws = readWorkspaceOrgIdFromStorage();
  if (ws) (headers as Record<string, string>)["X-Workspace-Organization-Id"] = ws;

  const r = await fetch(path, { ...init, headers });
  const text = await r.text();
  let json: unknown = null;
  try {
    json = JSON.parse(text) as T;
  } catch {
    // non-json error
  }
  if (!r.ok) {
    const err =
      json && typeof json === "object" && json !== null && "error" in json && typeof (json as { error: unknown }).error === "string"
        ? (json as { error: string }).error
        : text || `HTTP ${r.status}`;
    return { ok: false, status: r.status, error: err, body: json ?? undefined };
  }
  return { ok: true, status: r.status, data: json as T };
}

export function getManufacturingEntitlement() {
  return manufacturingFetch<MfgEntitlementResponse>("/api/mfg/entitlement", { method: "GET" });
}

export function listManufacturingMaterials() {
  return manufacturingFetch<MfgMaterialsResponse>("/api/mfg/materials", { method: "GET" });
}

export function listManufacturingMaterialsUsedInRecipes() {
  return manufacturingFetch<MfgMaterialsUsedInRecipesResponse>(
    "/api/mfg/materials/used-in-recipes",
    { method: "GET" },
  );
}

export function getManufacturingMaterialCostingSummary() {
  return manufacturingFetch<MfgMaterialCostingSummaryResponse>(
    "/api/mfg/materials/costing-summary",
    { method: "GET" }
  );
}

export function createMaterial(payload: {
  code?: string;
  name: string;
  unit: string;
  category: string;
  shipping_unit?: string;
  shipping_unit_weight?: number;
  pieces_per_shipping_unit?: number;
  plant_id?: string;
  /** Free-text primary vendor — persisted into `metadata.vendor`. */
  vendor?: string;
  /** VEN-2b: existing alias pick (wins over vendor string on save). */
  primary_alias_id?: string | null;
  /** MSDS-1: SDS requirement triage. */
  msds_status?: "not_required" | "undecided" | "required";
  /** Qty that triggers Fill from below-reorder (`metadata.reorder_threshold`). */
  reorder_threshold?: number | null;
  /** True when a $0 unit cost is intentional. */
  allow_zero_cost?: boolean;
}) {
  return manufacturingFetch<MfgCreateMaterialResponse>("/api/mfg/materials/create", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export function createMaterialsBulk(rows: MfgBulkImportRow[]) {
  return manufacturingFetch<MfgBulkCreateMaterialsResponse>("/api/mfg/materials/bulk-create", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ rows }),
  });
}

export function listManufacturingRecipeInventory() {
  return manufacturingFetch<MfgRecipeInventoryListResponse>("/api/mfg/recipes/inventory", {
    method: "GET",
  });
}

export function postManufacturingRecipeCostEntry(payload: MfgRecipeCostEntryRequest) {
  return manufacturingFetch<MfgRecipeCostEntryResponse>("/api/mfg/recipes/cost-entry", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export function listManufacturingAuditLog(filters: MfgAuditListFilters = {}) {
  return manufacturingFetch<MfgAuditListResponse>("/api/mfg/audit", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(filters),
  });
}

export function bulkUpdateMaterialCategory(payload: {
  material_ids: string[];
  category: string;
}) {
  return manufacturingFetch<{
    ok: true;
    updated_count: number;
    errors: Array<{ material_id: string; error: string }>;
  }>("/api/mfg/materials/bulk-update-category", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export function updateMaterial(payload: {
  material_id: string;
  name?: string;
  code?: string;
  unit?: string;
  category?: string;
  shipping_unit?: string;
  shipping_unit_weight?: number;
  pieces_per_shipping_unit?: number;
  is_active?: boolean;
  /**
   * Free-text primary vendor — persisted into `metadata.vendor`. Pass an
   * empty string to clear; omit to leave untouched.
   */
  vendor?: string;
  /** VEN-2b: existing alias pick or clear (null). */
  primary_alias_id?: string | null;
  /** MSDS-1: SDS requirement triage. */
  msds_status?: "not_required" | "undecided" | "required";
  /** Qty that triggers Fill from below-reorder. Null clears; omit to leave untouched. */
  reorder_threshold?: number | null;
  /** True when a $0 unit cost is intentional. */
  allow_zero_cost?: boolean;
}) {
  return manufacturingFetch<MfgUpdateMaterialResponse>("/api/mfg/materials/update", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export type MfgMaterialDeleteResponse = {
  ok?: boolean;
  error?: string;
  blockers?: Array<{ code: string; message: string }>;
};

/** Permanent delete for inactive materials (org admin/owner / platform). */
export function deleteMaterial(payload: { material_id: string }) {
  return manufacturingFetch<MfgMaterialDeleteResponse>("/api/mfg/materials/delete", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export function listManufacturingPlants() {
  return manufacturingFetch<MfgPlantsListResponse>("/api/mfg/plants", { method: "GET" });
}

export function createManufacturingPlant(payload: {
  plant_code: string;
  name: string;
  notes?: string;
  delivery_address?: string;
  is_active?: boolean;
  is_default?: boolean;
}) {
  return manufacturingFetch<MfgPlantCreateResponse>("/api/mfg/plants/create", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export function updateManufacturingPlant(payload: {
  plant_id: string;
  // D3l: plant_code is now editable so an org can rename the auto-seeded
  // 'DEFAULT' placeholder to a real code (e.g. 'CHEROKEE-PRIMARY') in
  // place. Backend surfaces a 409 if the new code collides with another
  // plant in the same org.
  plant_code?: string;
  name?: string;
  notes?: string;
  delivery_address?: string;
  is_active?: boolean;
}) {
  return manufacturingFetch<MfgPlantUpdateResponse>("/api/mfg/plants/update", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

// Slice 7: hard-delete a non-default plant. Service refuses defaults
// at the 409 layer; UI gates the action so the dropdown item is
// disabled when is_default=true. FK violations also return 409 with a
// human-readable hint about what to detach first (materials, dealer
// locations, etc.).
export function deleteManufacturingPlant(payload: { plant_id: string }) {
  return manufacturingFetch<MfgPlantDeleteResponse>("/api/mfg/plants/delete", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export function setDefaultManufacturingPlant(payload: { plant_id: string }) {
  return manufacturingFetch<MfgPlantUpdateResponse>("/api/mfg/plants/set-default", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export function listManufacturingUnits(filters?: {
  plant_id?: string;
  inventory_holder_org_id?: string;
  sale_path?: string;
}) {
  const q = new URLSearchParams();
  if (filters?.plant_id) q.set("plant_id", filters.plant_id);
  if (filters?.inventory_holder_org_id)
    q.set("inventory_holder_org_id", filters.inventory_holder_org_id);
  if (filters?.sale_path) q.set("sale_path", filters.sale_path);
  const qs = q.toString();
  const path = qs ? `/api/mfg/units?${qs}` : "/api/mfg/units";
  return manufacturingFetch<MfgUnitsListResponse>(path, { method: "GET" });
}

export function createManufacturingUnit(payload: {
  plant_id: string;
  recipe_id?: string;
  options?: Record<string, unknown>;
  sale_path?: "inventory_no_sale_yet" | "cash" | "finance" | "rto";
  inventory_holder_org_id?: string;
  end_customer_contact_id?: string;
  ops_deal_id?: string;
  visibility?: "private" | "org_internal" | "cross_dealer_marketplace";
  notes?: string;
}) {
  return manufacturingFetch<MfgUnitCreateResponse>("/api/mfg/units/create", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export type {
  ReceiptUploadMetadata,
  ReceiptUploadResult,
  MfgRecipeLinesMaterialReplaceResponse,
} from "@/lib/manufacturing/manufacturingClientReexports";

export {
  bulkCreateManufacturingRecipes,
  bulkUpdateManufacturingRecipesMeta,
  confirmManufacturingRecipesReview,
  bulkSetManufacturingPricingMargins,
  bulkSetManufacturingPricingLayerValues,
  bookManufacturingOverheadPeriodActual,
  closeManufacturingInventoryCountSession,
  completeManufacturingBuiltUnitBuild,
  createManufacturingBuildModel,
  createManufacturingBuildPipelinePhase,
  createManufacturingInventoryCountProposal,
  createManufacturingMaterialUse,
  createManufacturingMaterialIssuance,
  createManufacturingOverheadBucket,
  deleteManufacturingOverheadBucket,
  createManufacturingPricingLayerColumn,
  createManufacturingRecipe,
  deleteManufacturingBuildPipelinePhase,
  deleteManufacturingMaterialAlias,
  deleteManufacturingReceipt,
  bulkDeleteManufacturingRecipes,
  deleteManufacturingRecipe,
  dismissReceiptUnposted,
  downloadManufacturingPricingExport,
  extractManufacturingReceipt,
  finalizeManufacturingPeriodClose,
  getManufacturingMaterialUseDetail,
  getManufacturingMaterialsReconciliation,
  getManufacturingOverheadPeriodSummary,
  getManufacturingOverheadPeriodVariance,
  getManufacturingOverheadSnapshotRate,
  getManufacturingPricingNotificationSettings,
  getManufacturingRecipeDetail,
  getOpenManufacturingInventoryCountSession,
  linkCostEntryToReceipt,
  listManufacturingBuildModels,
  listManufacturingBuildPipelinePhases,
  listManufacturingInventoryChangelog,
  listManufacturingInventoryCountLines,
  listManufacturingInventoryCountProposals,
  listManufacturingMaterialAliases,
  listManufacturingMaterialUses,
  listManufacturingOrgSlotLabels,
  listManufacturingOverheadBuckets,
  listManufacturingPricingGrid,
  listManufacturingPricingLayerColumns,
  listManufacturingPricingLayersGrid,
  listManufacturingPtlDeliveryByPlant,
  refreshManufacturingPricingLayersMaterials,
  listManufacturingRecipeAttachments,
  createManufacturingRecipePendingLine,
  listManufacturingAllPendingLines,
  listManufacturingRecipePendingLineCounts,
  listManufacturingRecipePendingLines,
  listManufacturingReceipts,
  listManufacturingReceiptsAttention,
  listManufacturingRecipes,
  listManufacturingVendorSuggestions,
  openManufacturingInventoryCountSession,
  postMaterialCostEntry,
  previewManufacturingCogs,
  previewManufacturingPricingAlert,
  reconcileManufacturingRecipePendingLine,
  reopenManufacturingPeriodClose,
  replaceManufacturingRecipeLinesMaterial,
  resequenceManufacturingBuildPipelinePhases,
  reviewManufacturingInventoryCountProposal,
  saveManufacturingOverheadConsumption,
  setManufacturingOverheadPeriodAssumed,
  setManufacturingRecipeAttachments,
  setManufacturingRecipeLines,
  startManufacturingBuiltUnitBuild,
  startManufacturingPeriodClose,
  transitionManufacturingUnitPhase,
  updateManufacturingBuildModel,
  updateManufacturingBuildPipelinePhase,
  updateManufacturingInventoryCountLine,
  updateManufacturingMaterialUse,
  updateManufacturingMaterialUseMember,
  updateManufacturingOverheadBucket,
  updateManufacturingReceiptHeader,
  updateManufacturingRecipe,
  uploadManufacturingReceipt,
  upsertManufacturingMaterialAlias,
  upsertManufacturingMaterialUseMember,
  upsertManufacturingPtlDeliveryByModel,
  upsertManufacturingPricingLayerValue,
  upsertManufacturingPricingNotificationSettings,
} from "@/lib/manufacturing/manufacturingClientReexports";
